import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronUp, faChevronDown, faEye, faEyeSlash, faCalendar } from "@fortawesome/pro-solid-svg-icons";
import { Button } from "@perfectserve/library";
const CallFlowNarrative = ({ callFlow, isMinimal = false, interactionFinalAction, showExpandButton = false, }) => {
    var _a, _b, _c, _d;
    const [showRawData, setShowRawData] = useState(false);
    const [showExpanded, setShowExpanded] = useState(false);
    const extractScheduleNames = (callFlow) => {
        const scheduleNames = [];
        const scheduleNameMatches = callFlow.match(/ScheduleName:\s*([^;,\n]*)/g);
        if (scheduleNameMatches) {
            scheduleNameMatches.forEach((match) => {
                const scheduleName = match.replace(/^ScheduleName:\s*/, "").trim();
                if (scheduleName && !scheduleNames.includes(scheduleName)) {
                    scheduleNames.push(scheduleName);
                }
            });
        }
        return scheduleNames;
    };
    const scheduleNames = extractScheduleNames(callFlow);
    const initialRouting = {
        pso: ((_b = (_a = callFlow.split("Object:")[1]) === null || _a === void 0 ? void 0 : _a.split("Entering")[0]) === null || _b === void 0 ? void 0 : _b.trim()) || "",
        routingCard: callFlow.includes("Routing Prompt Card;")
            ? ((_d = (_c = callFlow.split("Routing Prompt Card;")[1]) === null || _c === void 0 ? void 0 : _c.split("Processing")[0]) === null || _d === void 0 ? void 0 : _d.trim()) || ""
            : "",
    };
    const wasTransferredToPText = callFlow.includes("Transferred to PText");
    const menuInteractions = callFlow
        .split("Processing Prompt Card:")
        .slice(1)
        .map((section) => {
        var _a, _b, _c, _d, _e, _f, _g;
        const menuName = ((_a = section.split(",")[0]) === null || _a === void 0 ? void 0 : _a.trim()) || "";
        const menuPrompt = section.includes("Menu Prompt:")
            ? ((_c = (_b = section.split("Menu Prompt:")[1]) === null || _b === void 0 ? void 0 : _b.split(",")[0]) === null || _c === void 0 ? void 0 : _c.trim()) || ""
            : "";
        const userChoice = section.includes("Prompt Card:")
            ? ((_e = (_d = section.split("Prompt Card:")[1]) === null || _d === void 0 ? void 0 : _d.split(",")[0]) === null || _e === void 0 ? void 0 : _e.trim()) || ""
            : "";
        const filters = section.includes("Prompt Card Filters:")
            ? ((_g = (_f = section.split("Prompt Card Filters:")[1]) === null || _f === void 0 ? void 0 : _f.split(";")[0]) === null || _g === void 0 ? void 0 : _g.trim()) || ""
            : "";
        return { menuName, menuPrompt, userChoice, filters };
    });
    const createFlowSequence = () => {
        const sequence = [];
        let stepCounter = 1;
        menuInteractions.forEach((interaction, index) => {
            sequence.push({
                type: "menu",
                data: interaction,
                stepNumber: stepCounter++,
            });
            const isLastMenu = index === menuInteractions.length - 1;
            const isBeforeTransferOrAgent = wasTransferredToPText ||
                (interactionFinalAction &&
                    (interactionFinalAction.includes("Transfer") ||
                        interactionFinalAction.includes("Agent") ||
                        interactionFinalAction.includes("Text")));
            if (isLastMenu && isBeforeTransferOrAgent && scheduleNames.length > 0) {
                scheduleNames.forEach((scheduleName) => {
                    sequence.push({
                        type: "schedule",
                        data: { scheduleName },
                        stepNumber: stepCounter++,
                    });
                });
            }
        });
        if (wasTransferredToPText) {
            sequence.push({
                type: "transfer",
                data: { action: "Transfer to Agent" },
                stepNumber: stepCounter++,
            });
        }
        sequence.push({
            type: "final",
            data: { action: interactionFinalAction || "Unknown" },
            stepNumber: stepCounter++,
        });
        return sequence;
    };
    const flowSequence = createFlowSequence();
    if (isMinimal) {
        return (_jsxs("div", { className: "d-flex align-items-center text-muted", style: {
                gap: "8px",
                fontSize: "14px",
            }, children: [_jsx("div", { className: "d-inline-flex align-items-center text-muted", style: {
                        gap: "8px",
                        fontSize: "14px",
                    }, children: flowSequence.map((step, index) => (_jsxs(React.Fragment, { children: [step.type === "menu" && (_jsx("span", { className: "ps-blue", style: {
                                    fontWeight: "500",
                                }, children: step.data.menuName })), step.type === "schedule" && (_jsxs("span", { className: "text-muted", style: {
                                    fontWeight: "400",
                                    fontSize: "12px",
                                    fontStyle: "italic",
                                }, title: step.data.scheduleName, children: [_jsx(FontAwesomeIcon, { icon: faCalendar, style: { marginRight: "4px" } }), "Schedule"] })), step.type === "transfer" && (_jsx("span", { className: "ps-teal", style: {
                                    fontWeight: "500",
                                }, children: step.data.action })), step.type === "final" && (_jsx("span", { className: "ps-teal", style: {
                                    fontWeight: "500",
                                }, children: step.data.action })), index < flowSequence.length - 1 && (_jsx("span", { className: "text-muted", style: { margin: "0 4px" }, children: "\u2192" }))] }, index))) }), showExpandButton && (_jsx(Button, { onClick: () => setShowExpanded(!showExpanded), size: "small", style: { marginLeft: "8px" }, children: _jsx(FontAwesomeIcon, { icon: showExpanded ? faChevronUp : faChevronDown }) })), showExpanded && (_jsx("div", { style: {
                        position: "absolute",
                        top: "100%",
                        left: "0",
                        right: "0",
                        zIndex: 1000,
                        backgroundColor: "white",
                        border: "1px solid #ddd",
                        borderRadius: "8px",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                        padding: "16px",
                        marginTop: "8px",
                    }, children: _jsx(CallFlowNarrative, { callFlow: callFlow, isMinimal: false, interactionFinalAction: interactionFinalAction, showExpandButton: false }) }))] }));
    }
    return (_jsxs("div", { className: "mt-15 p-15 bg-gray-100", style: {
            borderRadius: "8px",
        }, children: [_jsxs("div", { className: "d-flex justify-content-between align-items-center mb-15", children: [_jsx("h3", { style: { margin: 0, fontSize: "18px", fontWeight: "600" }, children: "Call Flow Narrative" }), _jsxs(Button, { onClick: () => setShowRawData(!showRawData), size: "small", children: [_jsx(FontAwesomeIcon, { icon: showRawData ? faEyeSlash : faEye }), showRawData ? "Hide Raw Data" : "Show Raw Data"] })] }), showRawData && (_jsx("div", { style: {
                    marginBottom: "24px",
                    padding: "16px",
                    backgroundColor: "#e9ecef",
                    borderRadius: "8px",
                    whiteSpace: "pre-wrap",
                    fontSize: "14px",
                    fontFamily: "monospace",
                }, children: callFlow })), _jsxs("div", { style: { marginBottom: "24px" }, children: [_jsx("h4", { className: "ps-blue", style: { margin: "0 0 8px 0", fontSize: "16px" }, children: "Initial Routing" }), _jsxs("div", { style: { marginLeft: "16px", fontSize: "14px" }, children: ["Practice: ", initialRouting.pso] })] }), flowSequence.map((step, index) => (_jsxs("div", { style: { marginBottom: "24px" }, children: [step.type === "menu" && (_jsxs(_Fragment, { children: [_jsxs("h4", { className: "ps-blue", style: { margin: "0 0 8px 0", fontSize: "16px" }, children: ["Step ", step.stepNumber, ": ", step.data.menuName] }), _jsxs("div", { style: { marginLeft: "16px" }, children: [_jsxs("div", { style: { fontSize: "14px", color: "#666", marginBottom: "8px" }, children: ["System Prompt: ", step.data.menuPrompt] }), _jsxs("div", { style: {
                                            display: "flex",
                                            alignItems: "center",
                                            marginTop: "8px",
                                            color: "var(--ps-primary-blue)",
                                            fontSize: "14px",
                                        }, children: [_jsx("span", { style: { marginRight: "8px" }, children: "\u2192" }), "Caller Selected: ", step.data.userChoice] }), step.data.filters && (_jsxs("div", { style: {
                                            marginTop: "8px",
                                            fontSize: "14px",
                                            color: "var(--ps-gray3)",
                                            fontStyle: "italic",
                                        }, children: ["Context: ", step.data.filters] }))] })] })), step.type === "schedule" && (_jsxs(_Fragment, { children: [_jsxs("h4", { className: "ps-blue", style: { margin: "0 0 8px 0", fontSize: "16px" }, children: ["Step ", step.stepNumber, ": Schedule Activation"] }), _jsx("div", { style: { marginLeft: "16px" }, children: _jsxs("div", { style: {
                                        fontSize: "14px",
                                        color: "#0066cc",
                                        padding: "8px 12px",
                                        backgroundColor: "#e8f4fd",
                                        borderRadius: "6px",
                                        border: "1px solid #b3d9ff",
                                        display: "inline-block",
                                    }, title: step.data.scheduleName, children: ["\uD83D\uDCC5", " ", (() => {
                                            let displayName = step.data.scheduleName;
                                            if (step.data.scheduleName.includes(" - ") && step.data.scheduleName.includes(":")) {
                                                const parts = step.data.scheduleName.split(" - ");
                                                if (parts.length >= 3 && parts[1] && parts[2]) {
                                                    const dept = parts[1].trim();
                                                    const eventPart = parts[2].split(":")[0];
                                                    const eventType = eventPart ? eventPart.trim() : parts[2].trim();
                                                    displayName = `${dept} - ${eventType}`;
                                                }
                                            }
                                            return displayName;
                                        })()] }) })] })), step.type === "transfer" && (_jsxs(_Fragment, { children: [_jsxs("h4", { className: "ps-blue", style: { margin: "0 0 8px 0", fontSize: "16px" }, children: ["Step ", step.stepNumber, ": ", step.data.action] }), _jsx("div", { style: { marginLeft: "16px", fontSize: "14px", color: "#00abc8" }, children: "Call transferred to agent for direct assistance" })] })), step.type === "final" && (_jsxs(_Fragment, { children: [_jsxs("h4", { className: "ps-blue", style: { margin: "0 0 8px 0", fontSize: "16px" }, children: ["Step ", step.stepNumber, ": Call Resolution"] }), _jsxs("div", { style: { marginLeft: "16px", fontSize: "14px", color: "#666" }, children: ["Final Action: ", step.data.action] })] }))] }, index)))] }));
};
export default CallFlowNarrative;
//# sourceMappingURL=CallFlowNarrative.js.map